import React, { createContext, useContext } from 'react'
import axios from 'axios'
import { 
  StockResponse, 
  WeatherResponse, 
  FruitCalculation, 
  CurrentEvent, 
  ItemInfo,
  PetWikiResponse,
  Pet,
  PetSearchFilters,
  PetComparison
} from '../types/api'

interface ApiContextType {
  isAuthenticated: boolean
  fetchStock: () => Promise<StockResponse>
  fetchWeather: () => Promise<WeatherResponse>
  calculateFruit: (params: {
    Name: string
    Weight: string
    Variant?: string
    Mutation?: string
  }) => Promise<FruitCalculation>
  fetchCurrentEvent: () => Promise<CurrentEvent>
  fetchItemInfo: (type?: string) => Promise<ItemInfo[]>
  fetchItemDetails: (itemId: string) => Promise<ItemInfo>
  getItemImage: (itemId: string) => string
  // Pet wiki functions
  fetchPetWiki: () => Promise<PetWikiResponse>
  fetchPetDetails: (petId: string) => Promise<Pet>
  searchPets: (filters: PetSearchFilters) => Promise<Pet[]>
  comparePets: (pet1Id: string, pet2Id: string) => Promise<PetComparison>
  getPetImage: (petId: string) => string
}

const ApiContext = createContext<ApiContextType | undefined>(undefined)

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider')
  }
  return context
}

interface ApiProviderProps {
  children: React.ReactNode
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  // Get API key from environment variable
  const apiKey = import.meta.env.VITE_API_KEY || ''
  const isAuthenticated = !!apiKey

  const api = axios.create({
    baseURL: 'https://api.joshlei.com/v2/growagarden',
    headers: {
      'jstudio-key': apiKey,
    },
  })

  const fetchStock = async (): Promise<StockResponse> => {
    const response = await api.get('/stock')
    return response.data
  }

  const fetchWeather = async (): Promise<WeatherResponse> => {
    const response = await api.get('/weather')
    return response.data
  }

  const calculateFruit = async (params: {
    Name: string
    Weight: string
    Variant?: string
    Mutation?: string
  }): Promise<FruitCalculation> => {
    const response = await api.get('/calculate', { params })
    return response.data
  }

  const fetchCurrentEvent = async (): Promise<CurrentEvent> => {
    try {
      const response = await api.get('/currentevent')
      return response.data
    } catch (error) {
      console.error('Error fetching current event:', error)
      throw error
    }
  }

  const fetchItemInfo = async (type?: string): Promise<ItemInfo[]> => {
    const params = type ? { type } : {}
    const response = await api.get('/info', { params })
    return response.data
  }

  const fetchItemDetails = async (itemId: string): Promise<ItemInfo> => {
    const response = await api.get(`/info/${itemId}`)
    return response.data
  }

  const getItemImage = (itemId: string): string => {
    return `https://api.joshlei.com/v2/growagarden/image/${itemId}`
  }

  // Pet wiki functions
  const fetchPetWiki = async (): Promise<PetWikiResponse> => {
    const response = await api.get('/petwiki')
    return response.data
  }

  const fetchPetDetails = async (petId: string): Promise<Pet> => {
    const response = await api.get(`/petwiki/${petId}`)
    return response.data
  }

  const searchPets = async (filters: PetSearchFilters): Promise<Pet[]> => {
    const response = await api.get('/petwiki/search', { params: filters })
    return response.data
  }

  const comparePets = async (pet1Id: string, pet2Id: string): Promise<PetComparison> => {
    const response = await api.get(`/petwiki/compare/${pet1Id}/${pet2Id}`)
    return response.data
  }

  const getPetImage = (petId: string): string => {
    return `https://api.joshlei.com/v2/growagarden/petimage/${petId}`
  }

  const value: ApiContextType = {
    isAuthenticated,
    fetchStock,
    fetchWeather,
    calculateFruit,
    fetchCurrentEvent,
    fetchItemInfo,
    fetchItemDetails,
    getItemImage,
    // Pet wiki functions
    fetchPetWiki,
    fetchPetDetails,
    searchPets,
    comparePets,
    getPetImage,
  }

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
} 